import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import { ReactComponent as CsrGeneratePending } from "../../assets/images/icons/csr-generate-pending.svg";
import { ReactComponent as CsrGenerationFailed } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as CsrDownload } from "../../assets/images/icons/download-white.svg";
import { ReactComponent as InfoUnfilled } from "../../assets/images/icons/info-unfilled.svg";
import { ReactComponent as CheckCircle } from "../../assets/images/icons/check-circle.svg";
import { ReactComponent as ArrowRight } from "../../assets/images/icons/arrow-right.svg";
import { CsrAPI } from "../../apis/CsrAPI";
import { LS_STATUS } from "../../utils/Constants";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";
import { ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";

const LaySummaryFile = () => {
  const { studyId } = useParams();
  const {
    errRef,
    errMsg,
    setErrMsg,
    downloadLatestFile,
    getLaySummaryStatus,
    stopStatusPolling,
    getLaySummaryInfo,
    currentLaySummaryName,
    currentRequestIdOfLaySummary,
    setCurrentRequestIdOfLaySummary,
    currentRequestTimeOfLaySummary,
    setCurrentRequestTimeOfLaySummary,
    mainStatusOfLaySummary,
    setMainStatusOfLaySummary,
    setCurrentTrafficLights,
  } = useCsr();
  const { hasPermission } = usePermissions();

  // ls:generation:create covers every button that starts a generation,
  // ls:generation:read the download of the rendered lay summary.
  const canGenerateLaySummary = hasPermission(PERMISSIONS.lsGenerationCreate);
  const canDownloadLaySummary = hasPermission(PERMISSIONS.lsGenerationRead);

  useEffect(() => {
    if (studyId && currentRequestIdOfLaySummary) {
      getLaySummaryStatus(studyId, currentRequestIdOfLaySummary);
    } else if (studyId) {
      getLaySummaryInfo(studyId);
    }
    return () => {
      stopStatusPolling();
    };
  }, [
    studyId,
    currentRequestIdOfLaySummary,
    getLaySummaryStatus,
    getLaySummaryInfo,
    stopStatusPolling,
  ]);

  const handleRequestLaySummary = async event => {
    event.preventDefault();

    if (!canGenerateLaySummary) return;

    stopStatusPolling();
    setMainStatusOfLaySummary(LS_STATUS.generating);
    setCurrentTrafficLights([]);
    setCurrentRequestTimeOfLaySummary("");

    // For now, we'll use the same CSR API - this will be updated when Lay Summary specific APIs are available
    CsrAPI.generateLaySummary({ studyId })
      .then(response => {
        setErrMsg("");
        setCurrentRequestIdOfLaySummary(response.request_id);
        getLaySummaryStatus(studyId, response.request_id);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setMainStatusOfLaySummary(LS_STATUS.errored);
        }
        errRef.current?.focus();
      });
  };

  const handleDownloadLaySummary = async event => {
    event.preventDefault();

    if (!canDownloadLaySummary) return;

    const fileURL =
      "/lay-summaries/" +
      studyId +
      "/requests/" +
      currentRequestIdOfLaySummary +
      "/documents?document_type='lay-summary'";
    downloadLatestFile(
      studyId,
      currentLaySummaryName + "_LaySummary.docx",
      fileURL
    );
  };

  const PageContent = () => {
    return (
      <div className={"csr-file csr-file__wrapper"}>
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>

        {/* Study Name and Status Section */}
        <div className={"csr-file__study-header"}>
          <div className={"csr-file__study-name"}>{currentLaySummaryName}</div>
        </div>

        <div className="csr-file__status-container">
          <div className={"csr-file__status-info"}>
            <InfoUnfilled className={"csr-file__status-icon"} />
            <span>Status: {mainStatusOfLaySummary}</span>
          </div>

          {/* Last Generated Section */}
          {mainStatusOfLaySummary === LS_STATUS.ready &&
            currentRequestTimeOfLaySummary && (
              <div className={"csr-file__last-generated"}>
                <CheckCircle className={"csr-file__check-icon"} />
                <span>Last generated - {currentRequestTimeOfLaySummary}</span>
              </div>
            )}
        </div>

        {(mainStatusOfLaySummary === LS_STATUS.generating ||
          mainStatusOfLaySummary === LS_STATUS.aiSummaryReady ||
          !mainStatusOfLaySummary) && (
          <>
            <div className={"csr-file__generate-component"}>
              <div className={"csr-file__generate-component__left"}>
                <span className={"csr-file__generate-component__status"}>
                  <CsrGeneratePending />
                </span>
                <span className={"csr-file__generate-component__label"}>
                  Generating Lay Summary
                </span>
              </div>
            </div>
            <button
              className={
                "input-submit input-submit__primary input-submit__arrow"
              }
              onClick={handleDownloadLaySummary}
              disabled={true}>
              Download Lay Summary file <CsrDownload />
            </button>
          </>
        )}

        {mainStatusOfLaySummary === LS_STATUS.ready && (
          <>
            <div className={"csr-file__action-buttons pb-0"}>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow"
                }
                onClick={handleDownloadLaySummary}
                disabled={!canDownloadLaySummary}
                title={canDownloadLaySummary ? undefined : NO_PERMISSION_TITLE}>
                Download Lay Summary file <CsrDownload />
              </button>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                onClick={handleRequestLaySummary}
                disabled={!canGenerateLaySummary}
                title={canGenerateLaySummary ? undefined : NO_PERMISSION_TITLE}>
                Generate again <ArrowRight />
              </button>
            </div>
            <p className="table-analysis-ai-response__disclaimer">
              Part of this document was created by Generative AI. Please review
              for clinical accuracy and refine the text as needed.
            </p>
          </>
        )}

        {mainStatusOfLaySummary === LS_STATUS.readyToStart && (
          <div className={"csr-file__action-buttons"}>
            <button
              className={
                "input-submit input-submit__primary input-submit__arrow"
              }
              onClick={handleRequestLaySummary}
              disabled={!canGenerateLaySummary}
              title={canGenerateLaySummary ? undefined : NO_PERMISSION_TITLE}>
              Generate Lay Summary <ArrowRight />
            </button>
          </div>
        )}

        {mainStatusOfLaySummary === LS_STATUS.errored && (
          <>
            <div className={"csr-file__generate-component"}>
              <div className={"csr-file__generate-component__left"}>
                <span className={"csr-file__generate-component__status"}>
                  <CsrGenerationFailed />
                </span>
                <span className={"csr-file__generate-component__label"}>
                  Lay Summary generation failed {currentRequestTimeOfLaySummary}
                </span>
              </div>
              <div className={"csr-file__generate-component__right"}>
                <button
                  className={
                    "input-submit input-submit__tertiary input-submit__small"
                  }
                  onClick={handleRequestLaySummary}
                  disabled={!canGenerateLaySummary}
                  title={
                    canGenerateLaySummary ? undefined : NO_PERMISSION_TITLE
                  }>
                  Try to generate again
                </button>
              </div>
            </div>
          </>
        )}

        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Generate & Download Lay Summary"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Wizard Questions",
        buttonGoToAction: "/ls/" + studyId + "/lay-summary-questions",
      }}
      nextButton={false}
    />
  );
};

export default LaySummaryFile;
